import { Grid, IconButton, LinearProgress, Tooltip } from '@mui/material';
import React, { FC } from 'react';
import { DataGrid, GridColumns, GridEventListener, GridRowClassNameParams, GridRowId, GridRowModes, GridRowModesModel, GridRowParams, GridValueGetterParams, MuiEvent } from '@mui/x-data-grid';
import { percentageFormater } from '../../../../Helpers/PercentageFormatter';
import { IGoalAndTargetRow } from '../../../Common/WorkforceProfile/GoalsAndTargetsCommon';
import { WorkForceGoalAndTargetRow } from '../../../../Component/Model/Enum/EmploymentEquity';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import EditIcon from "@mui/icons-material/Edit";
import AppAccordion from '../../../../Component/Common/Components/Accordion/AppAccordion';

interface IGridProps {
    title: string;
    isDisabled: boolean;
    rows: IGoalAndTargetRow[];
    processRowUpdate: (newRow: any, oldRow: any) => Promise<any>;
    isLoading: boolean;
}

const renderCell = (params: any) => {
    var isGapRow = params.row.label.toString().toLowerCase().includes('gap');
    var nonDecimalValue = parseFloat(params.value.toString());

    const outString = params.value.toString().replace(/[%]/gi, '');
    const value = parseFloat(outString);

    if(isGapRow && (value > 0 || nonDecimalValue > 0)){
        return <span style={{ color: "green" }}>{params.value}</span>;
    }
    if (value < 0) {
        return <span style={{ color: "red" }}>{params.value}</span>;
    }
    return <span>{params.value}</span>;
}

const checkRowType = (id: number, includeActual: boolean): boolean => {
    return (
        id === WorkForceGoalAndTargetRow.Year_One ||
        id === WorkForceGoalAndTargetRow.Year_Two ||
        id === WorkForceGoalAndTargetRow.Year_Three ||
        id === WorkForceGoalAndTargetRow.Year_Four ||
        id === WorkForceGoalAndTargetRow.Goal_Year_Five ||
        (includeActual && (id === WorkForceGoalAndTargetRow.Actual || id === WorkForceGoalAndTargetRow.BaselineSnapshot))
    )
}

//to determine if total row is required
function getTotalRowValue(params: GridValueGetterParams) {
    if (checkRowType(params.row.id, true)) {
        let sumMale = params.row.maleAfricanValue + params.row.maleColouredValue + params.row.maleIndianValue + params.row.maleWhiteValue;
        let sumFemale = params.row.femaleAfricanValue + params.row.femaleColouredValue + params.row.femaleIndianValue + params.row.femaleWhiteValue;
        let sumForeign = params.row.maleForeignNationalValue + params.row.femaleForeignNationalValue;
        return sumMale + sumFemale + sumForeign;
    }
    return '';
}

//to determine row value format
function getCellRowValue(params: GridValueGetterParams) {
    switch (params.row.id) {
        case WorkForceGoalAndTargetRow.EapTargetPercentage:
        case WorkForceGoalAndTargetRow.ActualHeadcountAcheived:
        case WorkForceGoalAndTargetRow.EapGapPercentage:
        case WorkForceGoalAndTargetRow.SectoralTargetPercentage:
        case WorkForceGoalAndTargetRow.SectoralGapPercentage:
        case WorkForceGoalAndTargetRow.YearOneHeadcountAcheived:
        case WorkForceGoalAndTargetRow.YearTwoHeadcountAcheived:
        case WorkForceGoalAndTargetRow.YearThreeHeadcountAchieved:
        case WorkForceGoalAndTargetRow.YearFourHeadcountAchieved:
        case WorkForceGoalAndTargetRow.GoalYearFiveHeadcountAchieved: {
            return percentageFormater(params.value, true, 2);
        }
        default: {
            return params.value;
        }
    }
}

function getRowDisplayType(params: GridRowClassNameParams<IGoalAndTargetRow>): string {

    if (checkRowType(params.row.id, true)) {
        return 'row--1';
    }

    return 'row--2';
}

export const GoalsAndTargetsGrid: FC<IGridProps> = (props) => {

    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});

    const handleEditClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleSaveClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleCancelClick = (id: GridRowId) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });
    };

    const handleRowEditStart = (
        params: GridRowParams,
        event: MuiEvent<React.SyntheticEvent>
    ) => {
        event.defaultMuiPrevented = true;
    };

    const handleRowEditStop: GridEventListener<"rowEditStop"> = (
        params,
        event
    ) => {
        event.defaultMuiPrevented = true;
    };

    const columns: GridColumns = [
        {
            field: 'label', headerName: '', minWidth: 170, editable: false, sortable: false,
            renderCell: (params: any) => (
                <Tooltip title={params.value}>
                    <span style={{ fontWeight: "bolder" }}>{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: 'maleAfricanValue', headerName: 'African Male', editable: true, type: 'number', sortable: false, valueGetter: getCellRowValue, flex: 1, minWidth: 72, resizable: true, headerAlign:"left",
            renderCell: renderCell
        },
        {
            field: 'maleColouredValue', headerName: 'Coloured Male', editable: true, type: 'number', sortable: false, valueGetter: getCellRowValue, flex: 1, minWidth: 72, resizable: true,  headerAlign:"left",
            renderCell: renderCell
        },
        {
            field: 'maleIndianValue', headerName: 'Indian Male', editable: true, type: 'number', sortable: false, valueGetter: getCellRowValue, flex: 1, minWidth: 72, resizable: true,  headerAlign:"left",
            renderCell: renderCell
        },
        {
            field: 'maleWhiteValue', headerName: 'White Male', editable: true, type: 'number', sortable: false, valueGetter: getCellRowValue, flex: 1, minWidth: 72, resizable: true,  headerAlign:"left",
            renderCell: renderCell
        },
        {
            field: 'femaleAfricanValue', headerName: 'African Female', editable: true, type: 'number', sortable: false, valueGetter: getCellRowValue, flex: 1, minWidth: 72, resizable: true,  headerAlign:"left",
            renderCell: renderCell
        },
        {
            field: 'femaleColouredValue', headerName: 'Coloured Female', editable: true, type: 'number', sortable: false, valueGetter: getCellRowValue, flex: 1, minWidth: 72, resizable: true,  headerAlign:"left",
            renderCell: renderCell
        },
        {
            field: 'femaleIndianValue', headerName: 'Indian Female', editable: true, type: 'number', sortable: false, valueGetter: getCellRowValue, flex: 1, minWidth: 72, resizable: true,  headerAlign:"left",
            renderCell: renderCell
        },
        {
            field: 'femaleWhiteValue', headerName: 'White Female', editable: true, type: 'number', sortable: false, valueGetter: getCellRowValue, flex: 1, minWidth: 72, resizable: true,  headerAlign:"left",
            renderCell: renderCell
        },
        {
            field: 'maleForeignNationalValue', headerName: 'Foreign National Male', editable: true, type: 'number', sortable: false, valueGetter: getCellRowValue, flex: 1, minWidth: 72, resizable: true,  headerAlign:"left",
            renderCell: renderCell
        },
        {
            field: 'femaleForeignNationalValue', headerName: 'Foreign National Female', editable: true, type: 'number', sortable: false, valueGetter: getCellRowValue, flex: 1, minWidth: 72, resizable: true,  headerAlign:"left",
            renderCell: renderCell
        },
        {
            field: 'Total', headerName: 'Total', editable: false, maxWidth: 72, type: 'number', sortable: false, valueGetter: getTotalRowValue, flex: 1, minWidth: 60, resizable: true,  headerAlign:"left",
            renderCell: renderCell
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            cellClassName: 'actions',
            minWidth: 70,
            maxWidth: 70,  
            headerAlign:"left",
            flex: 1,
            getActions: ({ id, row }) => {
                if (checkRowType(row.id, false)) {
                  const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
                  if (isInEditMode) {
                    return [
                      <Tooltip key="save" title="Save">
                        <IconButton onClick={handleSaveClick(id)} color="secondary">
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>,
                      <Tooltip key="cancel" title="Cancel">
                        <IconButton onClick={handleCancelClick(id)} color="error">
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>,
                    ];
                  }
            
                  return [
                    <Tooltip key="edit" title="Edit">
                      <IconButton onClick={handleEditClick(id)} color="inherit">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>,
                  ];
                }
                return [<></>];            
            }
        }
    ];

    return (
        <AppAccordion expandOnDefault={true} label={!props.isDisabled ? props.title : props.title + " - Disabled"}>
            <Grid container width="100%">
                <DataGrid
                    headerHeight={72}
                    sx={{
                        "& .MuiDataGrid-main": {
                            fontSize: 12,
                        },
                        "& .row--2": {
                            backgroundColor: "#e7e6e6",
                        },
                        "& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell": {
                            borderTop: "1px solid",
                            borderRight: "1px solid"
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            whiteSpace: "break-spaces",
                            lineHeight: 1
                        },
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "transparent"
                        },
                        "& .MuiInputBase-input": {
                            fontSize: 12
                        }
                    }}
                    disableSelectionOnClick
                    autoHeight
                    hideFooter
                    disableColumnFilter
                    disableColumnMenu
                    disableColumnSelector
                    rowHeight={25}
                    rows={props.rows}
                    columns={columns}
                    editMode="row"
                    rowModesModel={rowModesModel}
                    onRowEditStart={handleRowEditStart}
                    onRowEditStop={handleRowEditStop}
                    isCellEditable={(params) => (checkRowType(params.row.id, false))}
                    getRowClassName={(params: GridRowClassNameParams<IGoalAndTargetRow>) => getRowDisplayType(params)}
                    experimentalFeatures={{ newEditingApi: true }}
                    processRowUpdate={props.processRowUpdate}
                    components={{
                        LoadingOverlay: LinearProgress,
                    }}
                    loading={props.isLoading}
                    style={{ flexGrow: 1 }}
                />
            </Grid>
        </AppAccordion>
    )
}
